import debug from "@cher-ami/debug"
import Intro from "~/components/intro/Intro"
import {
	defaultPlayIn,
	defaultPlayInTranslate,
	defaultPlayOut,
	defaultPlayOutTranslate
} from "~/helpers/defaultTransitions"
import { isMobile } from "~/helpers/isMobile"
import { Component } from "~/libs/compose"
import SmoothScrollManager from "~/services/SmoothScrollManager"
const log = debug(`front:HomePage`)

type TStaticProps = {}

/**
 * @name HomePage
 */
export default class HomePage extends Component<TStaticProps> {
	public static attrName = "HomePage"

	private touchStartY = 0
	private firstStoryUrl: string
	canOpenFirstStory: boolean = false
	canOpenFirstStoryTimeout: null | ReturnType<typeof setTimeout> = null

	// ----------------------------------------------------------------------------- COMPONENTS

	public intro = this.add(Intro)

	// ----------------------------------------------------------------------------- LIFECYCLE

	public mounted() {
		log("> mounted")

		this.firstStoryUrl = this.getFirstStoryUrl()
		this.attachEvents()

		// TODO: check if working with cache
		// prefetch next story with a delay
		setTimeout(() => {
			this.prefetchFirstStory(this.firstStoryUrl)
		}, 2000)
	}

	public unmounted() {
		this.detachEvents()
		this.resetPreScrollIntro()
	}

	// ----------------------------------------------------------------------------- EVENTS

	protected attachEvents() {
		// cross browser wheel event
		window.addEventListener("wheel", this.wheelHandler)
		window.addEventListener("mousewheel", this.wheelHandler)
		// mobile touchmove event
		window.addEventListener("touchstart", this.touchStartHandler)
		window.addEventListener("touchmove", this.wheelHandler)
	}

	protected detachEvents() {
		window.removeEventListener("wheel", this.wheelHandler)
		window.removeEventListener("mousewheel", this.wheelHandler)
		window.removeEventListener("touchstart", this.touchStartHandler)
		window.removeEventListener("touchmove", this.wheelHandler)

		this.resetPreScrollIntro()
	}

	protected touchStartHandler = (e: TouchEvent) => {
		this.touchStartY = e.touches[0].pageY
	}

	protected wheelHandler = (e: WheelEvent | TouchEvent) => {
		// open first story if deltaY > 8 and we are at the bottom of the page
		const scrollDelta = (e as WheelEvent)?.deltaY
		const touchDelta = this.touchStartY - (e as TouchEvent)?.touches?.[0]?.pageY
		const deltaY = scrollDelta || touchDelta
		const tresholdY = e.type === "touchmove" ? 9 : 10

		// if we are at the bottom of the page
		if (
			window.scrollY + window.innerHeight >= document.body.offsetHeight ||
			window.innerHeight + window.scrollY >= document.body.offsetHeight
		) {
			// allow to open first story only if we are at the bottom of the page and after a timeout
			this.canOpenFirstStoryTimeout = setTimeout(() => {
				this.canOpenFirstStory = true
			}, 100)

			// allow to open directly the first story on desktop
			if (!isMobile()) {
				this.canOpenFirstStory = true
			}

			// open first story if deltaY > tresholdY
			if (this.canOpenFirstStory) {
				if (deltaY > tresholdY) {
					this.openFirstStory(this.firstStoryUrl)
				}

				this.preScrollIntro(deltaY, tresholdY)
			}
		} else {
			this.canOpenFirstStory = false
			clearTimeout(this.canOpenFirstStoryTimeout)
		}
	}

	preScrollIntro(deltaY: number, treshold) {
		this.intro.$root.style.transform = `translateY(${-deltaY / (isMobile() ? 1.2 : 2)}px)`

		setTimeout(() => {
			if (Math.abs(deltaY) < treshold * 1.2) {
				this.$root.style.transform = `translateY(0)`
			}
		}, 200)
	}

	resetPreScrollIntro() {
		this.intro.$root.style.transform = `translateY(0)`
	}

	// --------------------------------------------------------------------------- HISTORY CONTROL

	openFirstStory = (url: string) => {
		this.stack.handleHistory(url)
	}

	// prefetch next story for faster navigation
	prefetchFirstStory = async (url) => {
		await this.stack.fetchNewDocument(url, new AbortController())
	}

	getFirstStoryUrl = () => {
		const link = document.querySelector(".intro__link") as HTMLAnchorElement
		return link.href
	}

	// --------------------------------------------------------------------------- PAGE TRANSITION

	public playOut(goTo: string, resolve: () => void) {
		log("> playOut to go to", goTo)
		if (goTo === "SinglePostPage") {
			defaultPlayOutTranslate(this.$root, goTo, resolve)
		} else {
			defaultPlayOut(this.$root, goTo, resolve)
		}
	}

	public playIn(comeFrom: string, resolve: () => void) {
		// transform translateY(0)
		this.$root.style.transform = "translateY(0)"
		// translate if coming from singlePostPage else standard playin
		if (this.stack?.currentPage?.pageName === "SinglePostPage") {
			defaultPlayInTranslate(this.$root, comeFrom, resolve)
		} else {
			defaultPlayIn(this.$root, comeFrom, resolve)
		}
		this.intro.playIn(10)
		// restart lenis if it is locked
		if (SmoothScrollManager.lenis.isLocked) {
			SmoothScrollManager.start()
		}
	}
}
